/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DAILY_SIGNUPS_REPORT_ADMIN_PANEL
 * Date:         2026-03-08
 * Modified By:  Claude
 *
 * Description:
 * Admin panel to manually trigger the daily signups report with optional
 * date range. Displayed on the Admin Users page.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Adding: ~130 lines
 * - Projected: ~130 lines
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/services/schedulerApi.ts
 * - server/controllers/admin/schedulerController.js
 */

import React, { useState } from "react";
import { Card, Form } from "react-bootstrap";
import { notify } from "../../../utils/notifications";
import schedulerApi from "../../../services/schedulerApi";
import AdminButton from "../../../components/admin/AdminButton";

// [2026-03-08] Format Date to "YYYY-MM-DDThh:mm" in local timezone for datetime-local input
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getDefaultDates() {
  const now = new Date();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  return { start: toLocalInput(yesterday), end: toLocalInput(now) };
}

export default function AdminSignupReportPanel() {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // [2026-03-08] Quick presets: set date range based on common selections
  const applyPreset = (preset: string) => {
    const now = new Date();
    setEndDate(toLocalInput(now));

    switch (preset) {
      case "today": {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setStartDate(toLocalInput(start));
        break;
      }
      case "7days": {
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        setStartDate(toLocalInput(start));
        break;
      }
      case "30days": {
        const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        setStartDate(toLocalInput(start));
        break;
      }
      case "since_last":
      default:
        setStartDate("");
        setEndDate("");
        break;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const params: { start_date?: string; end_date?: string } = {};
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();

      const res = await schedulerApi.triggerSignupReport(params);
      notify.success(
        `Signup report scheduled (Job ID: ${res.data?.job_id?.slice(-6) || "..."})`
      );
    } catch (err: any) {
      notify.error(err, "Failed to trigger signup report");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <Card.Title className="mb-0">Generate Signup Report</Card.Title>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-column gap-3">
          <div className="small text-muted">
            Manually trigger a new-user signup report. Leave dates empty to use
            "since last report" (default daily behaviour).
          </div>

          {/* Quick presets */}
          <div className="d-flex flex-wrap gap-2">
            <AdminButton intent="secondary" size="sm" onClick={() => applyPreset("since_last")}>
              Since last report
            </AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => applyPreset("today")}>
              Today
            </AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => applyPreset("7days")}>
              Last 7 days
            </AdminButton>
            <AdminButton intent="secondary" size="sm" onClick={() => applyPreset("30days")}>
              Last 30 days
            </AdminButton>
          </div>

          {/* Date range */}
          <div className="d-flex gap-3 flex-wrap">
            <Form.Group style={{ flex: 1, minWidth: 180 }}>
              <Form.Label className="small">Date From</Form.Label>
              <Form.Control
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSubmitting}
              />
            </Form.Group>
            <Form.Group style={{ flex: 1, minWidth: 180 }}>
              <Form.Label className="small">Date To</Form.Label>
              <Form.Control
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSubmitting}
              />
            </Form.Group>
          </div>

          <div>
            <AdminButton intent="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </AdminButton>
          </div>

          <div className="small text-muted">
            Report will be emailed to <strong>info@uavtradezone.com</strong> (EMAIL_REPLY_TO_DEFAULT).
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
